import { useSyncExternalStore } from "react";
import { UserAccountEvents } from "../../main/user-area/types";
import { AuthCookies } from "../../main/user-area/utils/auth-cookies";
import { clearLumeEntry } from "../../main/utils/lume-entry";
import { clearCadernoStamp } from "./site-stamp";

// Same session as the rest of the site: the tm_uuid/tm_token cookies are the
// source of truth (set/cleared by the main app, validated server-side).
export function isLoggedIn(): boolean {
  return AuthCookies.validate(true);
}

export function getCredentials() {
  return {
    token: AuthCookies.getToken(),
    uuid: AuthCookies.getUUID(),
  };
}

// For when the server rejects the session (401): clears the cookies and the
// navbar's Redux state via the global the main bundle exposes (store.ts).
// Without this, stale cookies would keep isLoggedIn() true and the login
// page would bounce straight back, looping forever.
export function expireSession() {
  const userLogoutHandler = (window as any).userLogoutHandler;

  // O selo que o header lê é do aluno, não do browser: sem isto, o próximo a
  // entrar neste computador via "Continuar no Lume" para um caderno que não é
  // dele (lib/site-stamp.ts).
  clearCadernoStamp();
  clearLumeEntry();

  if (typeof userLogoutHandler === "function") {
    userLogoutHandler();
  } else {
    AuthCookies.logout();
  }
}

// A deliberate sign-out, from Lume's own account menu. Mechanically the same act
// as expireSession() — one shared session, cleared through the main bundle's
// store when it is on the page so the navbar and the persisted user go with it —
// but asked for the opposite reason, so the call site gets to say which it means.
export function logout() {
  expireSession();
}

// The main bundle announces auth changes as window events (see
// user-area/utils/analytics.ts); re-reading the cookies on each one keeps
// Lume in sync with the navbar without importing its Redux store.
const AUTH_EVENTS = [
  UserAccountEvents.LOGGED,
  UserAccountEvents.LOGOUT,
  UserAccountEvents.BOOTSTRAPPED,
];

export function subscribeToAuthChanges(onChange: () => void): () => void {
  AUTH_EVENTS.forEach((event) => window.addEventListener(event, onChange));
  return () => {
    AUTH_EVENTS.forEach((event) => window.removeEventListener(event, onChange));
  };
}

export function useIsLoggedIn(): boolean {
  return useSyncExternalStore(subscribeToAuthChanges, isLoggedIn);
}

// "Já entrou neste browser alguma vez", que é uma pergunta diferente de "vinha a
// caminho de algum lado" (o redirect do guard, presente em qualquer bounce). A
// tela de login usa isto para escolher entre a variante de regresso e a de quem
// nunca entrou; sobrevive ao logout de propósito — quem sai continua a ser um
// regresso da próxima vez.
const RETURNING_KEY = "lume.hasLoggedIn";

export function markHasLoggedIn(): void {
  try {
    window.localStorage.setItem(RETURNING_KEY, "1");
  } catch {
    // Store bloqueado: a tela fica na variante neutra, que é a segura.
  }
}

export function hasLoggedInBefore(): boolean {
  try {
    return window.localStorage.getItem(RETURNING_KEY) === "1";
  } catch {
    return false;
  }
}
